import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, ExternalLink, Building2, Calendar, Tag, DollarSign, 
  FileText, Paperclip, RefreshCw, Clock, AlertCircle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function TenderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reclassifying, setReclassifying] = useState(false);

  useEffect(() => {
    fetchTender();
  }, [id]);

  const fetchTender = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tenders/${id}`);
      if (!response.ok) {
        throw new Error('Tender not found');
      }
      const data = await response.json();
      setTender(data);
    } catch (error) {
      toast.error('Failed to load tender details');
      navigate('/tenders');
    } finally {
      setLoading(false);
    }
  };

  const handleReclassify = async () => {
    setReclassifying(true);
    try {
      const response = await fetch(`${API_URL}/api/tenders/${id}/reclassify`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        setTender(prev => ({
          ...prev,
          company: data.company,
          priority: data.priority,
          classification_rationale: data.rationale
        }));
        toast.success('Tender reclassified', {
          description: `Now classified as ${data.company} (${data.priority})`
        });
      } else {
        toast.error('Failed to reclassify');
      }
    } catch (error) {
      toast.error('Reclassification error');
    } finally {
      setReclassifying(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not specified';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (budget) => {
    if (!budget || !budget.amount) return null;
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: budget.currency || 'ETB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(budget.amount);
    return formatted;
  };

  if (loading) {
    return (
      <div data-testid="tender-detail-loading">
        <div className="section-header">
          <Skeleton className="h-8 w-48" />
        </div>
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-8 w-3/4 mb-4" />
            <Skeleton className="h-4 w-1/2 mb-2" />
            <Skeleton className="h-4 w-1/3 mb-6" />
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tender) {
    return (
      <div className="empty-state">
        <AlertCircle size={48} />
        <h3>Tender Not Found</h3>
        <Button onClick={() => navigate('/tenders')}>Back to Tenders</Button>
      </div>
    );
  }

  return (
    <div data-testid="tender-detail-page">
      {/* Header */}
      <div className="section-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Button variant="ghost" size="sm" onClick={() => navigate('/tenders')} data-testid="back-button">
            <ArrowLeft size={18} />
            Back
          </Button>
          <h2 className="section-title" style={{ margin: 0 }}>Tender Details</h2>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button variant="secondary" onClick={handleReclassify} disabled={reclassifying} data-testid="reclassify-button">
            <RefreshCw size={14} className={reclassifying ? 'animate-spin' : ''} />
            {reclassifying ? 'Reclassifying...' : 'Reclassify'}
          </Button>
          <Button asChild>
            <a href={tender.url} target="_blank" rel="noopener noreferrer" data-testid="view-original-button">
              <ExternalLink size={14} />
              View Original
            </a>
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 24 }}>
        {/* Left Column - Main Info */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Title Card */}
          <Card>
            <CardContent style={{ padding: 24 }}>
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <Badge className={`priority-${tender.priority?.toLowerCase()}`} data-testid="tender-priority">
                  {tender.priority || 'MEDIUM'}
                </Badge>
                <Badge className={`company-badge ${tender.company === 'ALEPHTAV' ? 'company-alephtav' : tender.company === 'MAU_TECH' ? 'company-mautech' : 'company-irrelevant'}`} data-testid="tender-company">
                  {tender.company || 'IRRELEVANT'}
                </Badge>
                {tender.has_full_details && (
                  <Badge variant="outline" className="text-green-600 border-green-300">
                    <CheckCircle size={12} className="mr-1" />
                    Full Details
                  </Badge>
                )}
              </div>
              
              <h1 style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.4, marginBottom: 16 }} data-testid="tender-title">
                {tender.title}
              </h1>

              {tender.classification_rationale && (
                <div style={{ 
                  padding: 12, 
                  background: 'hsl(215 90% 97%)', 
                  borderRadius: 8, 
                  fontSize: 13,
                  color: 'hsl(215 90% 30%)'
                }}>
                  <strong>Classification:</strong> {tender.classification_rationale}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Description */}
          {(tender.description || tender.scope) && (
            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FileText size={18} />
                  Description & Scope
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, color: 'hsl(var(--muted-foreground))' }} data-testid="tender-description">
                  {tender.description || tender.scope || 'No description available. Click "View Original" to see full details.'}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Requirements */}
          {tender.requirements && (
            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircle size={18} />
                  Requirements & Eligibility
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, color: 'hsl(var(--muted-foreground))' }} data-testid="tender-requirements">
                  {tender.requirements}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Attachments */}
          {tender.attachments && tender.attachments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Paperclip size={18} />
                  Attachments ({tender.attachments.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="tender-attachments">
                  {tender.attachments.map((attachment, index) => (
                    <a 
                      key={index}
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px 16px',
                        background: 'hsl(var(--muted))',
                        borderRadius: 8,
                        textDecoration: 'none',
                        color: 'hsl(var(--foreground))',
                        transition: 'background 0.15s'
                      }}
                      className="hover:bg-gray-200"
                    >
                      <div style={{
                        padding: '6px 10px',
                        background: 'hsl(var(--primary))',
                        color: 'white',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 600
                      }}>
                        {attachment.type || 'FILE'}
                      </div>
                      <span style={{ flex: 1, fontSize: 14 }}>{attachment.filename}</span>
                      <ExternalLink size={14} style={{ color: 'hsl(var(--muted-foreground))' }} />
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Meta Info */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Quick Info Card */}
          <Card>
            <CardHeader>
              <CardTitle style={{ fontSize: 16 }}>Quick Info</CardTitle>
            </CardHeader>
            <CardContent style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Buyer */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, color: 'hsl(var(--muted-foreground))', fontSize: 12, textTransform: 'uppercase' }}>
                  <Building2 size={14} />
                  Buyer / Organization
                </div>
                <div style={{ fontWeight: 500 }} data-testid="tender-buyer">
                  {tender.buyer || 'Not specified'}
                </div>
              </div>

              {/* Deadline */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, color: 'hsl(var(--muted-foreground))', fontSize: 12, textTransform: 'uppercase' }}>
                  <Calendar size={14} />
                  Deadline
                </div>
                <div style={{ fontWeight: 500 }} data-testid="tender-deadline">
                  {tender.deadline || 'Not specified'}
                </div>
              </div>

              {/* Category */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, color: 'hsl(var(--muted-foreground))', fontSize: 12, textTransform: 'uppercase' }}>
                  <Tag size={14} />
                  Category
                </div>
                <div style={{ fontWeight: 500 }} data-testid="tender-category">
                  {tender.category || 'General'}
                </div>
              </div>

              {/* Budget */}
              {tender.budget && tender.budget.amount && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, color: 'hsl(var(--muted-foreground))', fontSize: 12, textTransform: 'uppercase' }}>
                    <DollarSign size={14} />
                    Estimated Budget
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 18, color: 'hsl(142 72% 29%)' }} data-testid="tender-budget">
                    {formatCurrency(tender.budget)}
                  </div>
                  {tender.budget.raw_text && (
                    <div style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>
                      "{tender.budget.raw_text}"
                    </div>
                  )}
                </div>
              )}

              {/* Source */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, color: 'hsl(var(--muted-foreground))', fontSize: 12, textTransform: 'uppercase' }}>
                  <ExternalLink size={14} />
                  Source
                </div>
                <Badge variant="outline" data-testid="tender-source">
                  {tender.source || tender.site_name || 'Unknown'}
                </Badge>
              </div>

              {/* Captured At */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, color: 'hsl(var(--muted-foreground))', fontSize: 12, textTransform: 'uppercase' }}>
                  <Clock size={14} />
                  Captured
                </div>
                <div style={{ fontSize: 13 }} data-testid="tender-captured">
                  {formatDate(tender.captured_at)}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Reference IDs */}
          <Card>
            <CardHeader>
              <CardTitle style={{ fontSize: 16 }}>Reference</CardTitle>
            </CardHeader>
            <CardContent style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {tender.ref_id && (
                <div>
                  <div style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))', marginBottom: 2 }}>Tender Ref</div>
                  <code style={{ fontSize: 12, background: 'hsl(var(--muted))', padding: '4px 8px', borderRadius: 4 }}>
                    {tender.ref_id}
                  </code>
                </div>
              )}
              {tender.external_id && (
                <div>
                  <div style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))', marginBottom: 2 }}>External ID</div>
                  <code style={{ fontSize: 12, background: 'hsl(var(--muted))', padding: '4px 8px', borderRadius: 4 }}>
                    {tender.external_id}
                  </code>
                </div>
              )}
              <div>
                <div style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))', marginBottom: 2 }}>Internal ID</div>
                <code style={{ fontSize: 12, background: 'hsl(var(--muted))', padding: '4px 8px', borderRadius: 4 }}>
                  {tender._id}
                </code>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default TenderDetail;
